import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { useFormik } from "formik";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import useToast from "../../../helpers/useToast";
import useEncryption from "../../../services/useEncryption";
import { addDisplayDataApi } from "../../../api/userApi/userApi";
import { addDisTeamSchema } from "../../../services/formValidator/userValidator";
import { createAuditLogs } from "../../../api/providerApi/providerApi";
import { useSelector } from "react-redux";
import { selectAuth } from "../../../redux/slice/authSlice";
import useHeaders from "../../../helpers/useHeaders";
import { Country, State, City } from "country-state-city";
import { Autocomplete, Chip } from "@mui/material";
import path from "../../../path";

const UpdateLocDsplyModal = ({
  open,
  userId,
  handleClose,
  setUpdate,
  locDisData,
}) => {
  const { encryptData, decryptData } = useEncryption();
  const [profileImg, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const { id } = useParams();
  const toastOption = useToast();
  const decryptedId = decryptData(decodeURIComponent(id));
  const [loading, setLoading] = useOutletContext();
  const { role } = useSelector(selectAuth);
  const headers = useHeaders();

  const validateImage = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 1024 * 1024) {
        reject("File size should not be more than 1 MB");
        return;
      }
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.src = reader.result;
      };

      img.onload = () => {
        if (img.width !== 512 || img.height !== 512) {
          reject("Image dimensions should be 512x512 px");
        } else {
          resolve();
        }
      };

      img.onerror = () => {
        reject("Error reading image");
      };

      reader.readAsDataURL(file);
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        await validateImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhoto(reader.result);
          formik.setFieldValue("profileImg", file);
          setPhotoError("");
        };
        reader.readAsDataURL(file);
      } catch (error) {
        setPhotoError(error);
        formik.setFieldValue("profileImg", null);
      }
    }
  };

  const triggerFileUpload = () => {
    document.getElementById("file-upload").click();
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
      about_me: "",
      profileImg: null,
      insurance_details: [],
    },
    validationSchema: addDisTeamSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      const formData = new FormData();

      const selectedCountryName =
        Country.getCountryByCode(values.country)?.name || "";
      const selectedStateName =
        State.getStateByCodeAndCountry(values.state, values.country)?.name ||
        "";
      const selectedCityName = values.city;

      formData.append("firstName", values.firstName);
      formData.append("lastName", values.lastName);
      formData.append("streetAddress", values.streetAddress);
      formData.append("city", selectedCityName);
      formData.append("state", selectedStateName);
      formData.append("country", selectedCountryName);
      formData.append("zipCode", values.zipCode);
      formData.append("aboutMe", values.about_me);
      formData.append("organizationId", userId);
      formData.append(
        "insurance_details",
        JSON.stringify(values.insurance_details)
      );

      // if (values.profileImg) {
      //   formData.append("profileImg", values.profileImg);
      // }
      if (values.profileImg) {
        formData.append("profileImg", values.profileImg);
      }

      try {
        let response = await addDisplayDataApi(formData);

        setLoading(false);
        handleClose();
        resetForm();
        setUpdate((prev) => !prev);

        let obj = {
          event: `${role} has added teams user ${values.firstName} ${values.lastName} .`,
        };
        await createAuditLogs({ data: encryptData(obj) }, headers);

        toast.success(response.data.message, toastOption);
      } catch (error) {
        setLoading(false);

        if (error.response) {
          toast.error(
            error.response.data.message || "An error occurred.",
            toastOption
          );
        } else {
          toast.error("Network error. Please try again.", toastOption);
        }
      }
    },
  });

  const { values, errors, touched, setFieldValue } = formik;

  useEffect(() => {
    if (locDisData) {
      if (locDisData?.doctor?.photo) {
        const imageUrl = `${path}/${locDisData?.doctor?.photo
          ?.split("/")
          .pop()}`;

        fetch(imageUrl)
          .then(async (response) => {
            const blob = await response.blob();
            const file = new File([blob], imageUrl.split("/").pop(), {
              type: blob.type,
            });
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            setPhoto(imageUrl);
            formik.setFieldValue("profileImg", dataTransfer.files[0]);
          })
          .catch((error) => console.error("Error fetching image:", error));
      }
      // Set the fields from `locDisData` including `country`, `state`, and `city`
      formik.setFieldValue("firstName", locDisData?.doctor?.firstName);
      formik.setFieldValue("lastName", locDisData?.doctor?.lastName);
      formik.setFieldValue("streetAddress", locDisData?.streetAddress);
      formik.setFieldValue("zipCode", locDisData?.zipCode);
      formik.setFieldValue("about_me", locDisData?.doctor?.aboutMe);
      formik.setFieldValue(
        "insurance_details",
        locDisData?.doctor?.insurance_details
      );

      // Find country by name and set its isoCode

      const selectedCountry = countries.find(
        (country) => country.name === locDisData?.country
      );
      formik.setFieldValue(
        "country",
        selectedCountry?.isoCode || locDisData?.country || ""
      );

      // Find state by name within the selected country and set its isoCode
      const selectedState = selectedCountry
        ? State.getStatesOfCountry(selectedCountry.isoCode).find(
            (state) => state.name === locDisData?.state
          )
        : null;
      formik.setFieldValue(
        "state",
        selectedState?.isoCode || locDisData?.state || ""
      );

      // Find city by name within the selected state
      const selectedCity = selectedState
        ? City.getCitiesOfState(
            selectedCountry.isoCode,
            selectedState.isoCode
          ).find((city) => city.name === locDisData?.city)
        : null;
      formik.setFieldValue(
        "city",
        selectedCity?.name || locDisData?.city || ""
      );
    }
  }, [locDisData]);

  // Get options for countries, states, and cities
  const countries = Country.getAllCountries();
  const states = values.country ? State.getStatesOfCountry(values.country) : [];
  const cities = values.state
    ? City.getCitiesOfState(values.country, values.state)
    : [];

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <div className="d-flex align-items-center justify-content-between">
        <DialogTitle id="customized-dialog-title">
          Edit Display Details
        </DialogTitle>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent dividers>
        <div className="uploadprofileimg">
          <div>
            <div className="profile-pic-container">
              <img
                src={
                  profileImg ? profileImg : "/profile.svg"
                }
                alt="Profile"
                className="profile-pic"
                style={{ width: "150px", height: "150px", borderRadius: "50%" }}
              />
            </div>
            <input
              type="file"
              id="file-upload"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
            <button onClick={triggerFileUpload} className="upload-button">
              Upload Image
            </button>
            {photoError && <div style={{ color: "red" }}>{photoError}</div>}
          </div>
          <div className="textsides">
            <h6>Update Profile Image</h6>
            <p>The Image should be 512*512 px and not bigger than 1 MB</p>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit} className="sectionalForms">
          <div className="row">
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <TextField
                  label="First Name"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <TextField
                  label="Last Name"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <TextField
                  label="StreetAddress"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  name="streetAddress"
                  value={formik.values.streetAddress}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.streetAddress &&
                    Boolean(formik.errors.streetAddress)
                  }
                  helperText={
                    formik.touched.streetAddress && formik.errors.streetAddress
                  }
                />
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <FormControl fullWidth size="small">
                  <InputLabel id="country-label">Country</InputLabel>

                  <Select
                    name="country"
                    label="Country"
                    value={values.country}
                    onChange={(e) => {
                      const country = e.target.value;
                      setFieldValue("country", country);
                      setFieldValue("state", ""); // Reset state and city on country change
                      setFieldValue("city", "");
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.isoCode} value={country.isoCode}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <div>{touched.country && errors.country}</div>
                </FormControl>
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <FormControl fullWidth size="small">
                  <InputLabel id="state-label">State</InputLabel>
                  <Select
                    name="state"
                    label="State"
                    value={values.state}
                    onChange={(e) => {
                      const state = e.target.value;
                      setFieldValue("state", state);
                      setFieldValue("city", ""); // Reset city on state change
                    }}
                  >
                    {states.map((state) => (
                      <MenuItem key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <div>{touched.state && errors.state}</div>
                </FormControl>
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <FormControl fullWidth size="small">
                  <InputLabel id="city-label">City</InputLabel>
                  <Select
                    name="city"
                    labelId="city-label"
                    id="city"
                    value={values.city}
                    onChange={(e) => setFieldValue("city", e.target.value)}
                    label="City"
                  >
                    <MenuItem value="">Select City</MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city.name} value={city.name}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <div>{touched.city && errors.city}</div>
                </FormControl>
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <TextField
                  label="ZipCode"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  name="zipCode"
                  value={formik.values.zipCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.zipCode && Boolean(formik.errors.zipCode)
                  }
                  helperText={formik.touched.zipCode && formik.errors.zipCode}
                />
              </div>
            </div>
            <div className="col-lg-6 col-sm-12">
              <div className="formdesign">
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]} // No predefined options; user can add any tag
                  value={formik.values.insurance_details}
                  onChange={(event, newValue) =>
                    formik.setFieldValue("insurance_details", newValue)
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Add Insurance Types"
                      placeholder="Type and press Enter"
                      size="small"
                      error={
                        formik.touched.insurance_details &&
                        Boolean(formik.errors.insurance_details)
                      }
                      helperText={
                        formik.touched.insurance_details &&
                        formik.errors.insurance_details
                      }
                    />
                  )}
                />
              </div>
            </div>
            <div className="col-lg-12 col-sm-12">
              <div className="formdesign">
                <TextField
                  label="About me"
                  className="w-100"
                  variant="outlined"
                  size="small"
                  name="about_me"
                  multiline
                  rows={4} // Number of rows you want to show
                  value={formik.values.about_me}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.about_me && Boolean(formik.errors.about_me)
                  }
                  helperText={formik.touched.about_me && formik.errors.about_me}
                />
              </div>
            </div>
          </div>
          
        </form>
      </DialogContent>
      <DialogActions>
        <div className="text-end modalactions">
          <button
            className="btn btn-blacked outline me-2"
            onClick={handleClose}
            type="button"
          >
            Cancel
          </button>
          <button className="btn btn-blacked" onClick={formik.handleSubmit} type="submit">
            Submit
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateLocDsplyModal;
